import './Sessions.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  Header,
  InlineHeader,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { Api } from '$api';
import { useTranslator } from '$hooks';
import { Loader } from '$shared/components';
import { IcClose28 } from '$assets';
import { toastActions } from '$store/toast';
import { SessionModel } from '$store/models';
import { _confirm, format } from '$utils';

export const Sessions: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('loading');
  const [sessions, setSessions] = useState<SessionModel[]>([]);

  const loadData = useCallback(() => {
    setStatus('loading');
    Api.post('/profile/sessions')
      .then((resp) => {
        const data: any = resp;
        setSessions(data.sessions);
        setStatus('');
      })
      .catch(() => {
        setStatus('failed');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const handleTerminate = (sessionId: number) => () => {
    _confirm(t('sessions_session_terminate_confirm'), () => {
      dispatch(toastActions.setToastLoading());
      Api.post(`/profile/session/${sessionId}/terminate`)
        .then(() => {
          dispatch(toastActions.hideToast());
          setSessions((sessions) => sessions.filter((item) => item.id !== sessionId));
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    });
  };

  const sessionsPrepared = useMemo(() => {
    let current: SessionModel;
    let other: SessionModel[] = [];
    for (let item of sessions) {
      if (item.isCurrent) {
        current = item;
      } else {
        other.push(item);
      }
    }

    return {
      current: current!,
      other,
    };
  }, [sessions]);

  function renderItem(item: SessionModel) {
    const ts = item.lastUpdate * 1000;
    const isOnline = Date.now() - ts < 10 * 60 * 1000;
    return (
      <ListItem
        key={item.id}
        description={
          isOnline
            ? 'online'
            : t('sessions_session_label', {
                date: format(new Date(ts), 'd MMM H:s'),
              })
        }
        after={
          !item.isCurrent && (
            <Button
              size="small"
              icon={<IcClose28 />}
              mode="secondary"
              onClick={handleTerminate(item.id)}
            />
          )
        }
      >
        {item.device}
      </ListItem>
    );
  }

  function renderContent() {
    if (status === 'loading') {
      return <Loader isWrapped />;
    }

    if (status === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <div className="Sessions__cont">
        <InlineHeader>{t('sessions_current_title')}</InlineHeader>
        <List>{renderItem(sessionsPrepared.current)}</List>
        {sessionsPrepared.other.length > 0 && (
          <>
            <div className="Sessions__separator" />
            <InlineHeader>{t('sessions_other_title')}</InlineHeader>
            <List>{sessionsPrepared.other.map((item) => renderItem(item))}</List>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <Header showBackButton>{t('sessions_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
